import { IWebflowBenefitCategory } from "@wingspanhq/cms/dist/lib/interfaces/webflow/benefits";
import { WSElement, WSPage, WSText } from "@wingspanhq/fe-component-library";
import React from "react";
import { RouteComponentProps } from "react-router";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../../components/BrowserPageTitle/BrowserPageTitle";
import { Tabs } from "../../../../components/Tabs";
import { useCMSWebflowBenefits } from "../../../../query/cms/queries";
import { useUserId } from "../../../../query/hooks/helpers";
import { useSubscriptionV3Query } from "../../../../query/subscriptions/queries";
import { WSQueries } from "../../../../query/WSQuery";
import styles from "../Benefits.module.scss";
import { BenefitsCMSBlock } from "../BenefitsCMSBlock";
import { PremiumPerksPageHeader } from "../../components/PremiumPerksPageHeader";
import { selectorBenefitsList } from "../../selectors/selectorBenefitsList";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { PATH_BENEFITS_PERKS } from "../../paths";
import { BenefitsPerksTab } from "./contsants";
import { ExtendedWebflowBenefit } from "../../types";

export const RouteAllMyBenefits: React.FC<RouteComponentProps> = () => {
  useBrowserPageTitle("Benefits - All Premium Perks");
  const history = useHistory();
  const userId = useUserId();
  const qSubscription = useSubscriptionV3Query(userId);
  const qCMSWebflowBenefits = useCMSWebflowBenefits();
  const qFeatureFlags = useFeatureFlags();

  return (
    <WSPage>
      <PremiumPerksPageHeader />
      <WSQueries
        queries={{
          qCMSWebflowBenefits,
          qFeatureFlags
        }}
        renderLoader={() => <WSPage />}
      >
        {({ qCMSWebflowBenefitsData, qFeatureFlagsData }) => {
          const financialAndLegalBenefits = selectorBenefitsList({
            list: qCMSWebflowBenefitsData,
            isAdditional: false,
            categories: [
              IWebflowBenefitCategory.Finances,
              IWebflowBenefitCategory.Legal
            ],
            userId,
            subscription: qSubscription.data,
            featureFlag: qFeatureFlagsData,
            showMasterPolicyEnrollment: false
          });

          const wellnessBenefits = selectorBenefitsList({
            list: qCMSWebflowBenefitsData,
            isAdditional: false,
            categories: [
              IWebflowBenefitCategory.Wellness,
              IWebflowBenefitCategory.Other
            ],
            userId,
            subscription: qSubscription.data,
            featureFlag: qFeatureFlagsData,
            showMasterPolicyEnrollment: false
          });

          const healthBenefits = selectorBenefitsList({
            list: qCMSWebflowBenefitsData,
            isAdditional: false,
            categories: [IWebflowBenefitCategory.Health],
            userId,
            subscription: qSubscription.data,
            featureFlag: qFeatureFlagsData,
            showMasterPolicyEnrollment: false
          });

          const tabs: {
            slug: BenefitsPerksTab;
            label: string;
            list: ExtendedWebflowBenefit[];
            description: React.ReactNode;
          }[] = [
            {
              slug: "legal-and-financial-support",
              label: "Legal and financial support",
              list: financialAndLegalBenefits,
              description: (
                <WSText my="2XL" className={styles.text}>
                  Through Wingspan, you can get help with basic legal matters
                  and work with a financial wellness coach to guide you through
                  life’s toughest logistics.
                </WSText>
              )
            },
            {
              slug: "mind-and-body-support",
              label: "Mind and body support",
              list: wellnessBenefits,
              description: (
                <WSText my="2XL" className={styles.text}>
                  Get discounts on alternative medicine and chiropractic through
                  Wingspan.{" "}
                  <b>
                    Keep an eye out for Therapy Benefits as well, coming soon!
                  </b>
                </WSText>
              )
            },
            {
              slug: "save-money-on-medical-bills",
              label: "Save money on medical bills",
              list: healthBenefits,
              description: (
                <WSText my="2XL" className={styles.text}>
                  Skip the doctor and get free 24/7 access to virtual care, 1:1
                  support negotiating insurance bills, savings on prescriptions,
                  and more included in your Wingspan Membership.
                </WSText>
              )
            }
          ];

          const activeTab = tabs.find(({ slug }) =>
            history.location.pathname.includes(slug)
          );

          return (
            <>
              <Tabs
                tabs={tabs.map(tab => ({
                  label: tab.label,
                  path: `${PATH_BENEFITS_PERKS}/${tab.slug}`
                }))}
              />
              {activeTab?.description}
              <Switch>
                {tabs.map(tab => (
                  <Route
                    path={`${PATH_BENEFITS_PERKS}/${tab.slug}`}
                    exact
                    render={() => (
                      <WSElement>
                        {tab.list.map(benefit => (
                          <BenefitsCMSBlock
                            benefit={benefit}
                            subscription={qSubscription.data}
                            pagePath={`${PATH_BENEFITS_PERKS}/${tab.slug}/${benefit.slug}`}
                          />
                        ))}
                      </WSElement>
                    )}
                  />
                ))}

                <Redirect
                  from="*"
                  to={`${PATH_BENEFITS_PERKS}/${tabs[0].slug}`}
                />
              </Switch>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
